import React, { useState } from "react";
import { Plus, Info } from "lucide-react";
import { Card, CardContent, CardHeader } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { PackageType, getPackageConfig, formatPrice } from "../lib/package-config";
import { cn } from "../lib/utils";
import { PackageDetailsModal } from "./package-details-modal";
import { FeatureTooltip } from "./feature-tooltip";

interface PackageCardProps {
  packageType: PackageType;
  price: number;
  pricePerShow: number;
  selectedShowCount: number;
  onAddToProposal: (packageType: PackageType, price: number) => void;
  disabled?: boolean;
  isCalculating?: boolean;
  className?: string;
  showPricing?: boolean; // Control pricing visibility
}

export function PackageCard({
  packageType,
  price,
  pricePerShow,
  selectedShowCount,
  onAddToProposal,
  disabled = false,
  isCalculating = false,
  className,
  showPricing = true // Default to showing pricing
}: PackageCardProps) {
  const config = getPackageConfig(packageType);
  const Icon = config.icon;
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Dynamic color classes based on package type
  const colorClasses = {
    primary: {
      'set-core': 'text-gray-600',
      'set-audio': 'text-blue-600',
      'set-video': 'text-purple-600',
      'custom': 'text-green-600'
    },
    border: {
      'set-core': 'border-gray-200',
      'set-audio': 'border-blue-200',
      'set-video': 'border-purple-200',
      'custom': 'border-green-200'
    },
    button: {
      'set-core': '',
      'set-audio': '',
      'set-video': 'bg-purple-600 hover:bg-purple-700',
      'custom': 'bg-green-600 hover:bg-green-700'
    }
  };

  const hasSelectedShows = selectedShowCount > 0;

  return (
    <div className="pt-4"> {/* Add consistent top margin for all cards to accommodate badges */}
      <Card className={cn(
        "relative flex flex-col h-full",
        colorClasses.border[packageType],
        className
      )}>
        {/* Package Badge */}
        {config.badge && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
            <Badge className={cn(
              "text-white px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap",
              packageType === 'set-core' && "bg-gray-600",
              packageType === 'set-audio' && "bg-blue-600",
              packageType === 'set-video' && "bg-gradient-to-r from-purple-600 to-blue-600",
              packageType === 'custom' && "bg-green-600"
            )}>
              {config.badge}
            </Badge>
          </div>
        )}
        
        {/* Header with consistent background and spacing */}
        <div className={cn(
          "min-h-[140px] pb-2", // Ensure consistent height and bottom padding for all cards
          config.colorScheme.gradient 
            ? `bg-gradient-to-br ${config.colorScheme.gradient}` 
            : "bg-gray-50" // Subtle gray background for GA package
        )}>
          <CardHeader className="text-center pb-2 pt-6 relative">
            <div 
              className="cursor-pointer group transition-all duration-200"
              onClick={() => setIsModalOpen(true)}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Icon className={cn("w-5 h-5", colorClasses.primary[packageType])} />
                <h3 className={cn(
                  "text-lg font-semibold transition-all duration-200 group-hover:underline",
                  colorClasses.primary[packageType]
                )}>
                  {config.name}
                </h3>
                <Info className={cn(
                  "w-4 h-4 ml-1 transition-all duration-200 opacity-60 group-hover:opacity-100",
                  colorClasses.primary[packageType]
                )} />
              </div>
              <p className="text-sm text-muted-foreground h-8 flex items-center justify-center group-hover:text-gray-600 transition-colors duration-200">
                {config.subtitle}
              </p>
            </div>
          </CardHeader>
        
        {/* Pricing */}
        <div className="text-center mb-4 px-6 min-h-[80px] flex flex-col justify-center relative">
          {showPricing ? (
            <>
              <div className="flex items-baseline justify-center gap-1">
                <span className={cn("text-3xl font-bold", colorClasses.primary[packageType])}>
                  {isCalculating ? "Calculating..." : (hasSelectedShows ? formatPrice(price) : "--")}
                </span>
              </div>
              <div className="text-sm text-muted-foreground mt-1 h-5">
                {!isCalculating && hasSelectedShows && price > 0 ? (
                  <span>{formatPrice(pricePerShow)}/show avg</span>
                ) : (
                  !isCalculating && !hasSelectedShows && <span className="text-xs">per package</span>
                )}
              </div>

              {/* Overlay for pricing section only when no shows selected */}
              {!hasSelectedShows && (
                <div className="absolute inset-0 bg-white/50 backdrop-blur-md rounded-lg flex items-center justify-center">
                  <p className="text-gray-700 text-sm font-medium px-2 text-center">Select shows for pricing</p>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center">
              <span className={cn("text-lg font-semibold", colorClasses.primary[packageType])}>
                Contact for Pricing
              </span>
              <span className="text-xs text-muted-foreground mt-1">
                Custom rates available
              </span>
            </div>
          )}
        </div>
      </div>
      
      {/* Show count badge */}
      <div className="relative">
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <Badge className={cn(
            "text-white px-3 py-1 rounded-full text-xs font-medium",
            packageType === 'set-video'
              ? "bg-gradient-to-r from-purple-500 to-blue-500 shadow-lg border border-white relative overflow-hidden whitespace-nowrap"
              : packageType === 'set-audio'
              ? "bg-blue-600"
              : packageType === 'custom'
              ? "bg-green-600"
              : "bg-gray-600"
          )}>
            {packageType === 'set-video' && (
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-blue-400/20 animate-pulse"></div>
            )}
            <div className={packageType === 'set-video' ? "relative" : ""}>
              {selectedShowCount} show{selectedShowCount !== 1 ? 's' : ''} selected
            </div>
          </Badge>
        </div>
      </div>
      
      {/* Features and Button */}
      <CardContent className="flex-1 flex flex-col relative pt-6">
        <div className="space-y-2 text-sm text-muted-foreground mb-4 flex-1">
          {config.features.map((feature, index) => (
            <div key={index} className="flex items-start">
              <span className={cn(
                "mr-2",
                feature.highlight && colorClasses.primary[packageType],
                feature.highlight && "font-bold"
              )}>•</span>
              <FeatureTooltip
                text={feature.text}
                title={feature.details?.title}
                description={feature.details?.description}
                imageUrl={feature.details?.imageUrl}
                isDynamic={feature.isDynamic}
                dynamicText={feature.dynamicText}
                selectedShowCount={selectedShowCount}
                className={cn(
                  feature.highlight && "font-bold",
                  feature.highlight && colorClasses.primary[packageType]
                )}
              />
            </div>
          ))}
        </div>
        
        <Button
          className={cn(
            "w-full relative z-10",
            colorClasses.button[packageType]
          )}
          variant={config.colorScheme.buttonVariant || 'default'}
          onClick={() => onAddToProposal(packageType, price)}
          disabled={disabled || selectedShowCount === 0 || (!showPricing ? false : !price)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add {selectedShowCount} show{selectedShowCount !== 1 ? 's' : ''} to Plan
        </Button>
      </CardContent>
    </Card>
    
    {/* Package Details Modal */}
    <PackageDetailsModal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      packageType={packageType}
      price={price}
      pricePerShow={pricePerShow}
      selectedShowCount={selectedShowCount}
      onAddToProposal={onAddToProposal}
    />
    </div>
  );
}